// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type RebalanceTrade } from '@shared/data-access/interfaces/api/ai/portfolio-rebalance';
import { type PositionsAnalysis } from '@shared/data-access/interfaces/redux/portfolio-state';
import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import { OperationType } from '@shared/data-access/interfaces/operation-type';
import { getPrimaryTicker, isCashTicker } from './trade-helpers';

/** Default values for Returns interface fields */
const DEFAULT_RETURNS = {
  inception_return: null,
  m1_return: 0,
  m3_return: 0,
  m6_return: 0,
  max_draw_down: null,
  y1_return: 0,
  y1_vol: 0,
  y10_return: null,
  y10_vol: null,
  y3_return: 0,
  y3_vol: 0,
  y5_return: 0,
  y5_vol: 0,
  ytd_return: 0,
  price: null,
  price_as_of: null,
} as const;

/** Default values for PositionsAnalysis required fields */
const DEFAULT_POSITION_FIELDS = {
  status: '',
  figi: '',
  ipo: '',
  sector: '',
  url_morningstar: '',
  url_company: '',
  url_logo: '',
  outstanding_balance: 0,
  weight: 0,
  delta_volume: 0,
  exchange: '',
  asset_class: '',
} as const;

/**
 * Maps a RebalanceTrade to PositionsAnalysis format. Enriches the trade with asset
 * information from the assets lookup.
 */
export const mapTradeToPosition = (
  trade: RebalanceTrade,
  assets: AssetsInfoResp[],
): PositionsAnalysis | null => {
  const primaryTicker = getPrimaryTicker(trade);

  // Skip if no valid ticker or if it's cash
  if (!primaryTicker || isCashTicker(primaryTicker)) {
    return null;
  }

  // Find matching asset
  const matchingAsset = assets.find(
    asset =>
      asset.ticker === primaryTicker ||
      asset.isin === primaryTicker ||
      (trade.to_ticker &&
        (asset.ticker === trade.to_ticker || asset.isin === trade.to_isin)),
  );

  // For TRANSFER operations, find the from_asset details
  let fromAssetDetails: AssetsInfoResp | undefined;
  if (trade.operation === OperationType.TRANSFER && trade.from_ticker) {
    fromAssetDetails = assets.find(
      asset =>
        asset.ticker === trade.from_ticker ||
        asset.isin === trade.from_isin ||
        asset.isin === trade.from_ticker,
    );
  }

  // Build base position with preserved transfer information and defaults
  const basePosition = {
    ...DEFAULT_RETURNS,
    ...DEFAULT_POSITION_FIELDS,
    ...trade,
    ticker: primaryTicker,
    isin:
      matchingAsset?.isin ?? trade.isin ?? trade.to_isin ?? trade.from_isin ?? '',
    name: matchingAsset?.name ?? trade.name ?? '',
    asset_type: matchingAsset?.asset_type,
    url_logo: (matchingAsset as Stock | undefined)?.url_logo ?? '',
    url_morningstar: (matchingAsset as Stock | undefined)?.url_morningstar ?? '',
    dfi_url:
      matchingAsset && 'dfi_url' in matchingAsset
        ? matchingAsset.dfi_url
        : undefined,
    institution_id: trade.institution_id,
    institution_name: trade.institution_name,
    account_id: trade.account_id,
    // Preserve transfer fields for TRANSFER operations
    ...(trade.operation === OperationType.TRANSFER && {
      from_ticker: trade.from_ticker,
      from_isin: trade.from_isin,
      to_ticker: trade.to_ticker,
      to_isin: trade.to_isin,
      transfer_target: trade.to_ticker ?? trade.to_isin,
      // Store from_asset details for rendering
      from_asset_details: fromAssetDetails,
    }),
  };

  // Update ticker if matching asset found
  if (matchingAsset) {
    basePosition.ticker = matchingAsset.ticker;
  }

  return {
    ...basePosition,
    price: basePosition.price ?? 0,
  };
};
